import React, { useState, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import SEO from '../components/SEO';
import GalleryLightbox from '../components/GalleryLightbox';
import { BEFORE_AFTER_PAIRS as STATIC_BEFORE_AFTER_PAIRS, COMPANY_INFO as STATIC_COMPANY_INFO } from '../data/siteData';
import { useBeforeAfter, useCompanyInfo, usePageData } from '../lib/cms';

// Interactive Before/After Slider Component
function BeforeAfterSlider({ pair, index, onOpenLightbox }) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleTouchMove = (e) => {
    if (e.touches && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleMove(e.clientX);
    }
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  return (
    <div className="bg-white border border-zinc-300 shadow-md hover:shadow-xl transition-all flex flex-col overflow-hidden border-b-2 border-b-zinc-400 hover:border-b-[#F4AF00]">
      {/* Header Info */}
      <div className="p-5 bg-zinc-900 text-white flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2.5 h-2.5 rounded-full bg-[#F4AF00]" />
            <span className="text-[11px] font-black uppercase tracking-wider text-zinc-400">
              {pair.location || "Nordjylland"} &bull; Projekt #{index + 1}
            </span>
          </div>
          <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight text-white">
            {pair.title}
          </h3>
        </div>

        <button
          onClick={() => onOpenLightbox([pair.before, pair.after], 0)}
          className="px-3 py-1.5 bg-zinc-800 hover:bg-[#F4AF00] text-zinc-200 hover:text-[#121212] text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer"
          title="Se i fuld skærm"
        >
          <i className="fa-solid fa-magnifying-glass-plus text-xs" aria-hidden="true" />
          <span>Fuld skærm</span>
        </button>
      </div>

      {/* Interactive Slider Container */}
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        className="relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden select-none cursor-ew-resize bg-zinc-950"
      >
        {/* AFTER Image (Full Background) */}
        <img
          src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${pair.after.url.replace('https://assets.auxodev.dk/', '')}`}
          alt={pair.after.caption || `Efter - ${pair.title}`}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          loading="lazy"
        />

        {/* AFTER Label */}
        <div className="absolute top-3 right-3 z-10 bg-emerald-600/90 backdrop-blur-sm text-white px-3 py-1 text-xs font-black uppercase tracking-wider border border-emerald-500 shadow">
          <i className="fa-solid fa-check mr-1.5" aria-hidden="true" />
          Efter
        </div>

        {/* BEFORE Image (Clipped by sliderPosition) */}
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ width: `${sliderPosition}%` }}
        >
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${pair.before.url.replace('https://assets.auxodev.dk/', '')}`}
            alt={pair.before.caption || `Før - ${pair.title}`}
            className="absolute inset-0 w-full h-full object-cover max-w-none"
            style={{ width: containerRef.current ? `${containerRef.current.clientWidth}px` : '100%' }}
            loading="lazy"
          />
          {/* BEFORE Label */}
          <div className="absolute top-3 left-3 z-10 bg-zinc-900/90 backdrop-blur-sm text-white px-3 py-1 text-xs font-black uppercase tracking-wider border border-zinc-700 shadow">
            <i className="fa-solid fa-clock-rotate-left mr-1.5 text-[#F4AF00]" aria-hidden="true" />
            Før
          </div>
        </div>

        {/* Slider Divider Line */}
        <div
          className="absolute top-0 bottom-0 z-20 w-1 bg-white cursor-ew-resize shadow-[0_0_12px_rgba(0,0,0,0.8)]"
          style={{ left: `${sliderPosition}%` }}
          onMouseDown={handleMouseDown}
        >
          {/* Center Handle Button */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-[#F4AF00] text-[#121212] flex items-center justify-center shadow-2xl border-2 border-white pointer-events-auto cursor-grab active:cursor-grabbing">
            <i className="fa-solid fa-arrows-left-right text-xs sm:text-sm" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Footer Captions */}
      <div className="p-4 bg-zinc-50 border-t border-zinc-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div className="flex items-start gap-2 text-zinc-700">
          <span className="px-2 py-0.5 bg-zinc-200 text-zinc-900 font-bold uppercase text-[10px] rounded shrink-0">
            Før
          </span>
          <span className="italic">{pair.before.caption}</span>
        </div>
        <div className="flex items-start gap-2 text-zinc-700 sm:justify-end sm:text-right">
          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 font-bold uppercase text-[10px] rounded shrink-0 sm:order-last">
            Efter
          </span>
          <span className="font-semibold text-zinc-900">{pair.after.caption}</span>
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfterPage() {
  const { pairs: BEFORE_AFTER_PAIRS } = useBeforeAfter();
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const { page } = usePageData('foer-efter');
  const [viewMode, setViewMode] = useState('slider'); // 'slider' or 'side-by-side'
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeLightboxImages, setActiveLightboxImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const heroBg = page.heroImage || '';

  const openLightbox = (images, index = 0) => {
    setActiveLightboxImages(images);
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  const filteredPairs = BEFORE_AFTER_PAIRS.filter(pair => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'broenderslev') return pair.location?.toLowerCase().includes('brønderslev');
    if (selectedFilter === 'aalborg') return pair.location?.toLowerCase().includes('aalborg') || pair.location?.toLowerCase().includes('gug') || pair.location?.toLowerCase().includes('sønderholm');
    if (selectedFilter === 'frederikshavn') return pair.location?.toLowerCase().includes('frederikshavn') || pair.location?.toLowerCase().includes('skagen');
    if (selectedFilter === 'andre') return !pair.location?.toLowerCase().includes('brønderslev') && !pair.location?.toLowerCase().includes('aalborg') && !pair.location?.toLowerCase().includes('frederikshavn') && !pair.location?.toLowerCase().includes('gug') && !pair.location?.toLowerCase().includes('sønderholm') && !pair.location?.toLowerCase().includes('skagen');
    return true;
  });

  return (
    <>
      <SEO
        title="Før og Efter Billeder - CMSNord.dk"
        description="Se alle før- og efter-forvandlinger af havearbejde, belægning, terrænregulering og entreprenørarbejde i Brønderslev, Aalborg, Frederikshavn og hele Nordjylland."
        canonicalUrl={`${window.location.origin}/galleri/foer-efter`}
        ogImage={heroBg || BEFORE_AFTER_PAIRS[0]?.after?.url || ''}
      />

      {/* 1. PANORAMA HERO STRIP */}
      <section className="relative bg-[#121212] text-white py-16 sm:py-24 border-b border-zinc-800 overflow-hidden">
        {heroBg && (
          <div className="absolute inset-0 z-0">
            <img
              src={heroBg}
              alt="Før og efter forvandlinger hos CMSNord.dk"
              className="w-full h-full object-cover object-center opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/95 via-[#121212]/80 to-[#121212]/60" />
          </div>
        )}

        {/* Large Background Watermark Text */}
        <div className="absolute right-4 sm:right-12 -bottom-6 z-0 select-none pointer-events-none opacity-5 font-black text-7xl sm:text-9xl uppercase tracking-tighter text-white">
          FØR / EFTER
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          {/* Breadcrumb / Back Link */}
          <NavLink
            to="/galleri"
            className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#F4AF00] hover:text-white mb-4 transition-colors"
          >
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            <span>Tilbage til gallerioversigt</span>
          </NavLink>

          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F4AF00]/20 border border-[#F4AF00]/40 text-[#F4AF00] text-xs font-black uppercase tracking-wider mb-3">
            <i className="fa-solid fa-camera-retro" aria-hidden="true" />
            <span>{BEFORE_AFTER_PAIRS.length} Dokumenterede Projekter</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight">
            Før &amp; Efter Galleri
          </h1>
          <p className="text-zinc-300 text-sm sm:text-base mt-2 max-w-2xl">
            Se forvandlingen med egne øjne. Her har vi samlet vores før- og efter-projekter, der viser den markante forskel før og efter vores arbejde med belægning, haveanlæg, rydning og terrænregulering i Nordjylland.
          </p>
        </div>
      </section>

      {/* 2. TOOLBAR & VIEW CONTROLS */}
      <section className="py-6 bg-zinc-100 border-b border-zinc-300 sticky top-0 z-30 shadow-sm backdrop-blur-md bg-zinc-100/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Area Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setSelectedFilter('all')}
              className={`px-3 py-1.5 text-xs font-black uppercase tracking-wider transition-colors cursor-pointer border ${
                selectedFilter === 'all'
                  ? 'bg-[#121212] text-white border-[#121212]'
                  : 'bg-white text-zinc-700 hover:text-[#121212] border-zinc-300 hover:border-zinc-400'
              }`}
            >
              Alle ({BEFORE_AFTER_PAIRS.length})
            </button>
            <button
              onClick={() => setSelectedFilter('broenderslev')}
              className={`px-3 py-1.5 text-xs font-black uppercase tracking-wider transition-colors cursor-pointer border ${
                selectedFilter === 'broenderslev'
                  ? 'bg-[#121212] text-white border-[#121212]'
                  : 'bg-white text-zinc-700 hover:text-[#121212] border-zinc-300 hover:border-zinc-400'
              }`}
            >
              Brønderslev ({BEFORE_AFTER_PAIRS.filter(p => p.location?.toLowerCase().includes('brønderslev')).length})
            </button>
            <button
              onClick={() => setSelectedFilter('aalborg')}
              className={`px-3 py-1.5 text-xs font-black uppercase tracking-wider transition-colors cursor-pointer border ${
                selectedFilter === 'aalborg'
                  ? 'bg-[#121212] text-white border-[#121212]'
                  : 'bg-white text-zinc-700 hover:text-[#121212] border-zinc-300 hover:border-zinc-400'
              }`}
            >
              Aalborg / Gug ({BEFORE_AFTER_PAIRS.filter(p => p.location?.toLowerCase().includes('aalborg') || p.location?.toLowerCase().includes('gug') || p.location?.toLowerCase().includes('sønderholm')).length})
            </button>
            <button
              onClick={() => setSelectedFilter('frederikshavn')}
              className={`px-3 py-1.5 text-xs font-black uppercase tracking-wider transition-colors cursor-pointer border ${
                selectedFilter === 'frederikshavn'
                  ? 'bg-[#121212] text-white border-[#121212]'
                  : 'bg-white text-zinc-700 hover:text-[#121212] border-zinc-300 hover:border-zinc-400'
              }`}
            >
              Frederikshavn &amp; Skagen ({BEFORE_AFTER_PAIRS.filter(p => p.location?.toLowerCase().includes('frederikshavn') || p.location?.toLowerCase().includes('skagen')).length})
            </button>
            <button
              onClick={() => setSelectedFilter('andre')}
              className={`px-3 py-1.5 text-xs font-black uppercase tracking-wider transition-colors cursor-pointer border ${
                selectedFilter === 'andre'
                  ? 'bg-[#121212] text-white border-[#121212]'
                  : 'bg-white text-zinc-700 hover:text-[#121212] border-zinc-300 hover:border-zinc-400'
              }`}
            >
              Øvrige Vendsyssel
            </button>
          </div>

          {/* View Mode Toggle Buttons */}
          <div className="flex items-center gap-2 bg-zinc-200 p-1 rounded">
            <button
              onClick={() => setViewMode('slider')}
              className={`px-3.5 py-1.5 text-xs font-black uppercase tracking-wider rounded transition-all cursor-pointer flex items-center gap-1.5 ${
                viewMode === 'slider'
                  ? 'bg-[#F4AF00] text-[#121212] shadow-sm'
                  : 'text-zinc-700 hover:text-[#121212]'
              }`}
            >
              <i className="fa-solid fa-arrows-left-right" aria-hidden="true" />
              <span>Interaktiv Slider</span>
            </button>

            <button
              onClick={() => setViewMode('side-by-side')}
              className={`px-3.5 py-1.5 text-xs font-black uppercase tracking-wider rounded transition-all cursor-pointer flex items-center gap-1.5 ${
                viewMode === 'side-by-side'
                  ? 'bg-[#F4AF00] text-[#121212] shadow-sm'
                  : 'text-zinc-700 hover:text-[#121212]'
              }`}
            >
              <i className="fa-solid fa-columns" aria-hidden="true" />
              <span>Side om side</span>
            </button>
          </div>
        </div>
      </section>

      {/* 3. BEFORE & AFTER SHOWCASE */}
      <section className="py-12 sm:py-16 bg-[#F4F4F6] text-[#121212] border-b border-zinc-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
              Viser {filteredPairs.length} af {BEFORE_AFTER_PAIRS.length} forvandlinger
            </p>
            {selectedFilter !== 'all' && (
              <button
                onClick={() => setSelectedFilter('all')}
                className="text-xs font-bold text-[#D99B00] hover:underline cursor-pointer flex items-center gap-1"
              >
                <i className="fa-solid fa-rotate-left" aria-hidden="true" />
                <span>Nulstil filter</span>
              </button>
            )}
          </div>

          {viewMode === 'slider' ? (
            /* SLIDER VIEW MODE: 2 columns grid */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredPairs.map((pair, idx) => (
                <BeforeAfterSlider
                  key={pair.id || idx}
                  pair={pair}
                  index={idx}
                  onOpenLightbox={openLightbox}
                />
              ))}
            </div>
          ) : (
            /* SIDE BY SIDE VIEW MODE */
            <div className="space-y-12">
              {filteredPairs.map((pair, idx) => (
                <div
                  key={pair.id || idx}
                  className="bg-white border border-zinc-300 shadow-md p-6 border-b-2 border-b-zinc-400"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-6 border-b border-zinc-200">
                    <div>
                      <span className="text-xs font-black uppercase tracking-wider text-[#D99B00]">
                        Projekt #{idx + 1} &bull; {pair.location}
                      </span>
                      <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-[#121212]">
                        {pair.title}
                      </h3>
                    </div>

                    <button
                      onClick={() => openLightbox([pair.before, pair.after], 0)}
                      className="px-4 py-2 bg-zinc-900 hover:bg-[#F4AF00] text-white hover:text-[#121212] text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                      <span>Åbn billeder i stort format</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Before Image */}
                    <div
                      onClick={() => openLightbox([pair.before, pair.after], 0)}
                      className="group cursor-pointer space-y-2"
                    >
                      <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-900 border border-zinc-300">
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${pair.before.url.replace('https://assets.auxodev.dk/', '')}`}
                          alt={pair.before.caption}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3 bg-zinc-900/90 text-white px-3 py-1 text-xs font-black uppercase tracking-wider border border-zinc-700">
                          <i className="fa-solid fa-clock-rotate-left mr-1.5 text-[#F4AF00]" aria-hidden="true" />
                          Før
                        </div>
                      </div>
                      <p className="text-xs text-zinc-600 italic">
                        {pair.before.caption}
                      </p>
                    </div>

                    {/* After Image */}
                    <div
                      onClick={() => openLightbox([pair.before, pair.after], 1)}
                      className="group cursor-pointer space-y-2"
                    >
                      <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-900 border border-zinc-300">
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${pair.after.url.replace('https://assets.auxodev.dk/', '')}`}
                          alt={pair.after.caption}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 right-3 bg-emerald-600/90 text-white px-3 py-1 text-xs font-black uppercase tracking-wider border border-emerald-500">
                          <i className="fa-solid fa-check mr-1.5" aria-hidden="true" />
                          Efter
                        </div>
                      </div>
                      <p className="text-xs text-zinc-900 font-semibold">
                        {pair.after.caption}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 4. CALL TO ACTION / KONTAKT OS */}
      <section className="py-16 sm:py-20 bg-white text-[#121212] border-b border-zinc-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <div className="flex items-center justify-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[#F4AF00]" />
            <span className="text-xs font-black uppercase tracking-wider text-zinc-500">
              Skal vi skabe det næste resultat for dig?
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-black text-[#121212] uppercase tracking-tight">
            Giv din have eller indkørsel et markant løft
          </h2>

          <p className="text-zinc-700 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Vi udfører alt inden for professionel flisebelægning, terrænregulering, jordarbejde og totalrydning af haver i Brønderslev og omegn.
          </p>

          <div className="pt-4 flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="nowrap-contact px-8 py-3.5 bg-[#121212] hover:bg-zinc-800 text-white font-black text-sm uppercase tracking-wider transition-all shadow-lg hover:scale-105 flex items-center gap-3"
            >
              <i className="fa-solid fa-phone text-[#F4AF00] text-base" aria-hidden="true" />
              <span>Ring: {COMPANY_INFO.phone}</span>
            </a>

            <NavLink
              to="/kontakt"
              className="px-8 py-3.5 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-sm uppercase tracking-wider transition-all shadow-lg hover:scale-105 flex items-center gap-2"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </NavLink>
          </div>
        </div>
      </section>

      {/* Fullscreen Lightbox Modal */}
      <GalleryLightbox
        images={activeLightboxImages}
        isOpen={lightboxOpen}
        initialIndex={activeImageIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
